/**
 * @fileoverview Bank connection step for onboarding.
 * @module features/onboarding/components/steps/BankConnectionStep
 */

import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Plus } from 'lucide-react'
import Autoplay from 'embla-carousel-autoplay'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { ScrollReveal } from '@/shared/components/ui'
import { ConnectBankModal } from '@/features/bank-connections/components/ConnectBankModal'
import { useBankConnections } from '@/features/bank-connections/hooks/useBankConnections'

/** Bank logos to display in the carousel */
const BANK_LOGOS = [
  { name: 'PKO Bank Polski', src: '/bank-logos/PKO Bank Polski-logo.webp' },
  { name: 'mBank', src: '/bank-logos/mBank-logo.webp' },
  { name: 'Santander Bank Polska', src: '/bank-logos/Santander Bank Polska-logo.webp' },
  { name: 'Bank Pekao', src: '/bank-logos/Bank Pekao-logo.webp' },
  { name: 'Alior Bank', src: '/bank-logos/Alior Bank-logo.webp' },
  { name: 'BGZ BNP Paribas', src: '/bank-logos/BGZ BNP Paribas-logo.webp' },
  { name: 'Bank Millenium', src: '/bank-logos/Bank Millenium-logo.webp' },
]

interface BankConnectionStepProps {
  /** Callback when bank is connected successfully */
  onComplete: () => void
  /** Callback to skip this step */
  onSkip: () => void
}

/**
 * Seventh step of onboarding - Bank connection via PSD2 (optional).
 *
 * @component
 * @example
 * ```tsx
 * <BankConnectionStep onComplete={completeStep} onSkip={skipStep} />
 * ```
 */
export function BankConnectionStep({ onComplete, onSkip }: BankConnectionStepProps): ReactElement {
  const { t } = useTranslation('features-onboarding')
  const [modalOpen, setModalOpen] = useState(false)

  // Check if any bank is already connected
  const connectionsQuery = useBankConnections()
  const hasConnection = (connectionsQuery.data?.length ?? 0) > 0

  // If connected, proceed to next step
  const handleModalClose = (open: boolean): void => {
    setModalOpen(open)
    // After modal closes, check if connection was made
    if (!open) {
      connectionsQuery.refetch().then(() => {
        if ((connectionsQuery.data?.length ?? 0) > 0) {
          onComplete()
        }
      })
    }
  }

  return (
    <div className="flex flex-col items-center justify-center max-w-md mx-auto text-center">
      {/* Animated bank logo carousel */}
      <ScrollReveal animateOnMount blur delay={0}>
        <div
          className="w-32 h-32 rounded-2xl border border-border/15 flex items-center justify-center mb-6 overflow-hidden"
          style={{ backgroundColor: 'oklch(0.85 0 21)' }}
        >
          <Carousel
            opts={{ loop: true }}
            plugins={[Autoplay({ delay: 2000, stopOnInteraction: false })]}
            className="w-20 h-20"
          >
            <CarouselContent className="ml-0">
              {BANK_LOGOS.map((bank) => (
                <CarouselItem key={bank.name} className="pl-0 flex items-center justify-center">
                  <img
                    src={bank.src}
                    alt={bank.name}
                    className="w-20 h-20 object-contain"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </ScrollReveal>

      {/* Heading */}
      <ScrollReveal animateOnMount blur delay={0.1}>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4">
          {t('bank.title', 'Connect your bank')}
        </h2>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.15}>
        <p className="text-muted-foreground mb-8">
          {t('bank.subtitle', 'Automatically import transactions')}
        </p>
      </ScrollReveal>

      {/* Connect button */}
      <ScrollReveal animateOnMount blur delay={0.2} className="w-full">
        <div className="space-y-4">
          {hasConnection ? (
            <Button variant="outline" size="lg" onClick={onComplete} className="w-full">
              {t('bank.connected', 'Bank Connected - Continue')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button variant="outline" size="lg" onClick={() => setModalOpen(true)} className="gap-1.5">
              <Plus className="size-3.5" />
              {t('bank.connect', 'Connect')}
            </Button>
          )}

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground uppercase">
              {t('common.or', 'or')}
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <Button variant="ghost" onClick={onSkip} className="w-full opacity-50">
            {t('bank.skip', 'Skip for now')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </ScrollReveal>

      {/* Bank connection modal */}
      <ConnectBankModal
        open={modalOpen}
        onOpenChange={handleModalClose}
        defaultCountry="PL"
      />
    </div>
  )
}
